import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { applyAnnouncementPlaceholders } from "../../features/announcements/utils/announcementPlaceholders";

interface Props {
  subject: string;
  body: string;
  onBodyChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Placeholder chips plus the preview that shows what one recipient actually reads.
 * The preview uses a sample first name because every recipient sees a different
 * one — its job is to prove the placeholder resolves, not to show a real member.
 */
const AnnouncementPersonalization = ({ subject, body, onBodyChange, disabled }: Props) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);

  // Sample values, not real ones: every recipient resolves to a different name, so
  // the preview's job is to prove the placeholder resolves — not to show a member.
  const sampleName = t("announcements.previewSampleName", "Anna");
  const organizationName = t("announcements.previewSampleOrganization", "your studio");

  const placeholders = [
    { token: "{{firstName}}", label: t("announcements.placeholderFirstName", "First name") },
    { token: "{{organization}}", label: t("announcements.placeholderOrganization", "Organization") },
  ];

  const preview = (value: string) =>
    applyAnnouncementPlaceholders(value, sampleName, organizationName);

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-grey-40 dark:text-grey-80">
          {t("announcements.placeholdersLabel", "Insert:")}
        </span>
        {placeholders.map((placeholder) => (
          <button
            key={placeholder.token}
            type="button"
            disabled={disabled}
            className="rounded-full bg-grey-95 px-3 py-1 text-xs font-medium text-grey-30 hover:bg-grey-90 disabled:opacity-50 dark:bg-primary-20 dark:text-grey-70 dark:hover:bg-[#3E334D]"
            onClick={() => onBodyChange(body ? `${body}${placeholder.token}` : placeholder.token)}
          >
            {placeholder.label}
          </button>
        ))}
        <button
          type="button"
          className="ml-auto flex items-center gap-1 text-sm text-primary-40 hover:underline dark:text-primary-90"
          onClick={() => setShowPreview((current) => !current)}
        >
          {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
          {showPreview
            ? t("announcements.hidePreview", "Hide preview")
            : t("announcements.showPreview", "Preview")}
        </button>
      </div>

      {showPreview && (
        <div className="with-border mt-2 rounded-xl bg-grey-95 p-3 dark:bg-primary-20">
          <p className="text-xs uppercase tracking-wide text-grey-40 dark:text-grey-80">
            {t("announcements.previewNote", "How one recipient sees it")}
          </p>
          <p className="mt-2 text-sm font-semibold text-grey-20 dark:text-white">
            {preview(subject) || t("announcements.previewNoSubject", "(no subject yet)")}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-grey-30 dark:text-grey-70">
            {preview(body) || t("announcements.previewNoBody", "(no message yet)")}
          </p>
        </div>
      )}
    </div>
  );
};

export default AnnouncementPersonalization;
